import { useEffect, useRef } from 'react'
import { useMutation, useQuery } from 'convex/react'
import Toast from 'react-native-toast-message'
import { api } from '@ppwr/convex/_generated/api'
import { BADGES, isBadgeId, tierName } from '@ppwr/convex/badges/catalog'

const MAX_INDIVIDUAL_TOASTS = 3
// react-native-toast-message defaults topOffset to 30; add 15px below that
// baseline to keep badge unlocks clear of the Dynamic Island.
const BADGE_TOAST_TOP_OFFSET = 65
// Toast only tiers earned during (or just before) this session; older unseen
// tier-ups — offline earns, backfill — are absorbed silently.
const SESSION_GRACE_MS = 60_000

/**
 * Watches the caller's badge rows and toasts newly earned tiers (via the
 * app-wide react-native-toast-message setup), then persists seenTier
 * server-side so toasts never repeat across devices. Mount once, app-wide.
 */
export function useBadgeUnlockToasts() {
  const myBadges = useQuery(api.badges.queries.getMyBadges, {})
  const markBadgesSeen = useMutation(api.badges.mutations.markBadgesSeen)
  const sessionStartRef = useRef(Date.now())
  const inFlightRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!myBadges) return

    const unseen = myBadges.filter(
      (row) => isBadgeId(row.badgeId) && row.tierEarnedAt.length > row.seenTier && !inFlightRef.current.has(row.badgeId)
    )
    if (unseen.length === 0) return

    const sessionCutoff = sessionStartRef.current - SESSION_GRACE_MS
    const fresh = unseen.filter((row) => row.tierEarnedAt[row.tierEarnedAt.length - 1] >= sessionCutoff)

    if (fresh.length > MAX_INDIVIDUAL_TOASTS) {
      Toast.show({
        type: 'ppBadgeToast',
        text1: 'Badges earned!',
        text2: `You earned ${fresh.length} badges`,
        topOffset: BADGE_TOAST_TOP_OFFSET
      })
    } else {
      // react-native-toast-message shows one toast at a time; stagger so
      // multiple same-event unlocks each get a moment on screen.
      fresh.forEach((row, index) => {
        if (!isBadgeId(row.badgeId)) return
        const badge = BADGES[row.badgeId]
        const tier = row.tierEarnedAt.length
        const isOneShot = badge.tiers.length === 1
        setTimeout(() => {
          Toast.show({
            type: 'ppBadgeToast',
            text1: 'Badge earned!',
            text2: isOneShot ? badge.name : `${badge.name} — ${tierName(badge, tier)}`,
            // Badge art rendered as the toast's leading icon (see toastConfig ppBadgeToast).
            props: { badge, tier },
            visibilityTime: 3000,
            topOffset: BADGE_TOAST_TOP_OFFSET
          })
        }, index * 3200)
      })
    }

    // Mark everything unseen (fresh AND stale) so nothing re-fires later.
    for (const row of unseen) inFlightRef.current.add(row.badgeId)
    markBadgesSeen({ badgeIds: unseen.map((row) => row.badgeId) })
      .catch(() => {
        // On failure, allow a retry on the next query update.
      })
      .finally(() => {
        for (const row of unseen) inFlightRef.current.delete(row.badgeId)
      })
  }, [myBadges, markBadgesSeen])
}
